type Ctor<T extends Element = Element> = new (...args: never[]) => T;

// The type-filter argument shared by the query helpers: a single class, or a
// *non-empty* array of classes. An empty array can never match any element, so
// the `[Ctor, ...Ctor[]]` shape rejects it at compile time rather than silently
// narrowing to nothing.
type Type = Ctor | readonly [Ctor, ...Ctor[]];

// The element type a `Type` narrows to: the instance type of the class, or the
// union of instance types for an array of classes.
type Narrowed<T extends Type> = T extends readonly [Ctor, ...Ctor[]]
    ? InstanceType<T[number]>
    : T extends Ctor
      ? InstanceType<T>
      : never;

// Disambiguate the optional `(type?, parent?)` arguments shared by every query
// helper: a class or array of classes narrows the result by type, anything else
// is the search root (defaulting to `document`).
function resolveQuery(
    typeOrParent: ParentNode | Ctor | readonly Ctor[] | undefined,
    parent: ParentNode,
): { types: readonly Ctor[] | undefined; root: ParentNode } {
    const types: readonly Ctor[] | undefined = Array.isArray(typeOrParent)
        ? typeOrParent
        : typeof typeOrParent === 'function'
          ? [typeOrParent]
          : undefined;

    const root: ParentNode = types
        ? parent
        : ((typeOrParent as ParentNode | undefined) ?? document);

    return { types, root };
}

// Whether `el` is an instance of one of `types`; an absent `types` matches
// everything.
function matchesType(el: Element, types: readonly Ctor[] | undefined): boolean {
    return !types || types.some((t) => el instanceof t);
}

// Drop the elements that aren't one of `types`, keeping all of them when no
// type filter is given.
function filterByType(
    els: Element[],
    types: readonly Ctor[] | undefined,
): Element[] {
    return types ? els.filter((el) => matchesType(el, types)) : els;
}

/**
 * Find the first element matching `selector`, or `null` if there is none.
 *
 * Pass a `type` (a class or array of classes) to narrow the result to that
 * element type; a match that isn't an instance of one of them yields `null`.
 * The search is scoped to `parent`, defaulting to `document`.
 */
export function $(selector: string, parent?: ParentNode): Element | null;
export function $<T extends Type>(
    selector: string,
    type: T,
    parent?: ParentNode,
): Narrowed<T> | null;
export function $(
    selector: string,
    typeOrParent?: ParentNode | Ctor | readonly Ctor[],
    parent: ParentNode = document,
): Element | null {
    const { types, root } = resolveQuery(typeOrParent, parent);

    const el = root.querySelector(selector);
    if (el === null) return null;
    if (!matchesType(el, types)) return null;
    return el;
}

/**
 * Like `$`, but throws instead of returning `null`: an `Error` when nothing
 * matches `selector`, and a distinct one when a match isn't of the given `type`.
 */
export function $OrThrow(selector: string, parent?: ParentNode): Element;
export function $OrThrow<T extends Type>(
    selector: string,
    type: T,
    parent?: ParentNode,
): Narrowed<T>;
export function $OrThrow(
    selector: string,
    typeOrParent?: ParentNode | Ctor | readonly Ctor[],
    parent: ParentNode = document,
): Element {
    const { types, root } = resolveQuery(typeOrParent, parent);

    const el = root.querySelector(selector);
    if (el === null) {
        throw new Error(`No element matched selector: ${selector}`);
    }
    if (types && !matchesType(el, types)) {
        const expected = types.map((t) => t.name).join(' | ');
        throw new Error(
            `Element matched "${selector}" but was ${el.constructor.name}, expected ${expected}`,
        );
    }
    return el;
}

/**
 * Find every element matching `selector` as an array (empty if none).
 *
 * Pass a `type` (a class or array of classes) to keep only matches that are
 * instances of one of them. The search is scoped to `parent`, defaulting to
 * `document`.
 */
export function $$(selector: string, parent?: ParentNode): Element[];
export function $$<T extends Type>(
    selector: string,
    type: T,
    parent?: ParentNode,
): Narrowed<T>[];
export function $$(
    selector: string,
    typeOrParent?: ParentNode | Ctor | readonly Ctor[],
    parent: ParentNode = document,
): Element[] {
    const { types, root } = resolveQuery(typeOrParent, parent);

    return filterByType(Array.from(root.querySelectorAll(selector)), types);
}

/**
 * Like `$$`, but throws an `Error` when no element matches `selector` (after
 * any `type` filtering), so the result is always a non-empty array.
 */
export function $$OrThrow(selector: string, parent?: ParentNode): Element[];
export function $$OrThrow<T extends Type>(
    selector: string,
    type: T,
    parent?: ParentNode,
): Narrowed<T>[];
export function $$OrThrow(
    selector: string,
    typeOrParent?: ParentNode | Ctor | readonly Ctor[],
    parent: ParentNode = document,
): Element[] {
    const { types, root } = resolveQuery(typeOrParent, parent);

    const els = filterByType(
        Array.from(root.querySelectorAll(selector)),
        types,
    );

    if (els.length === 0) {
        throw new Error(`No element matched selector: ${selector}`);
    }

    return els;
}

// The query helpers handed to an $onload handler are the module-level `$` /
// `$OrThrow` / `$$` / `$$OrThrow` with the handler's selector pre-applied and
// the search root defaulted to the matched element, so they share those
// functions' exact signatures — including the optional type-narrowing argument.
// Their leading `string` is a selector *suffix* appended to the handler's
// selector, not a standalone selector.
type $onLoadHandlerArgs = {
    cn(suffix?: string): string;
    $: typeof $;
    $OrThrow: typeof $OrThrow;
    $$: typeof $$;
    $$OrThrow: typeof $$OrThrow;
};
type $onLoadHandler<E extends Element = Element> = (
    e: E,
    args: $onLoadHandlerArgs,
) => void;

// Curry one of the query helpers for an $onload handler: prepend `sel` to the
// caller's suffix and default the search root to `$el`, while preserving the
// helper's own `(selector, type?, parent?)` overloads.
function scopedQuery<F extends (selector: string, ...args: never[]) => unknown>(
    fn: F,
    sel: string,
    $el: Element,
): F {
    const query = (
        suffix: string,
        typeOrParent?: Ctor | readonly Ctor[] | ParentNode,
        parent?: ParentNode,
    ): unknown => {
        const selector = `${sel}${suffix}`;
        const call = fn as unknown as (
            selector: string,
            typeOrParent?: Ctor | readonly Ctor[] | ParentNode,
            parent?: ParentNode,
        ) => unknown;

        return Array.isArray(typeOrParent) || typeof typeOrParent === 'function'
            ? call(selector, typeOrParent, parent ?? $el)
            : call(selector, typeOrParent ?? $el);
    };

    return query as unknown as F;
}

/**
 * Run `fn` once for every element matching `selector`, both for elements
 * already present and for those added by the time the page finishes loading.
 *
 * Pass a `type` (a class or array of classes) to restrict the run to elements
 * of that type and narrow the handler's element argument accordingly. `fn` also
 * receives selector-relative `cn`/`$`/`$OrThrow`/`$$`/`$$OrThrow` helpers scoped
 * to the matched element by default.
 */
export function $onload(sel: string, fn: $onLoadHandler): void;
export function $onload<T extends Type>(
    sel: string,
    type: T,
    fn: $onLoadHandler<Narrowed<T>>,
): void;
export function $onload(
    sel: string,
    typeOrFn: Ctor | readonly Ctor[] | $onLoadHandler,
    fn?: $onLoadHandler,
): void {
    const handler = (fn ?? typeOrFn) as $onLoadHandler;
    const type = fn === undefined ? undefined : (typeOrFn as Type);

    const loader = () =>
        (type ? $$(sel, type) : $$(sel)).forEach(($el) =>
            handler($el, {
                cn: (suffix = '') => `${sel}${suffix}`,
                $: scopedQuery($, sel, $el),
                $OrThrow: scopedQuery($OrThrow, sel, $el),
                $$: scopedQuery($$, sel, $el),
                $$OrThrow: scopedQuery($$OrThrow, sel, $el),
            }),
        );

    if (document.readyState === 'complete') {
        queueMicrotask(loader);
    } else {
        window.addEventListener('load', () => queueMicrotask(loader));
    }
}

export function svgEl(
    tagname: string,
    atts: Record<string, string> = {},
    ...children: Node[]
): SVGElement {
    const el = document.createElementNS('http://www.w3.org/2000/svg', tagname);

    Object.entries(atts).forEach(([name, value]) => {
        el.setAttribute(name, value);
    });

    children.forEach((child) => {
        el.appendChild(child);
    });

    return el;
}

export function elFromHtml(html: string): ChildNode | null {
    const div = document.createElement('div');
    div.innerHTML = html.trim();

    return div.firstChild;
}
